import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Card from '@/components/ui/Card';
import { cn } from '@/lib/utils';

/**
 * Canonical dashboard stat tile — one metric with an optional trend and an
 * icon in a tinted box. Replaces the near-identical local `StatCard` copies in
 * the detail dashboards (AgentDashboard, advertising/AddieDashboard,
 * fundraising/FundraisingDashboard).
 *
 * Two presentations:
 *  - default (bare tile, no card border): drops into a `WidgetShell` grid on
 *    the home dashboard without nesting a Card in a Card.
 *  - `elevated`: a standalone `Card` (shadow, larger icon box, ring hover) for
 *    the detail pages' top KPI rows. Pass the icon with its own size class in
 *    this mode (e.g. `<Icon className="w-5 h-5" />`).
 *
 * Wrap it in a Link by passing `to`.
 */

export type StatTone =
  | 'green'
  | 'blue'
  | 'purple'
  | 'indigo'
  | 'orange'
  | 'red'
  | 'rose'
  | 'amber'
  | 'teal'
  | 'stone';

const toneStyles: Record<StatTone, string> = {
  green: 'bg-green-100 text-green-600',
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  orange: 'bg-orange-100 text-orange-600',
  red: 'bg-red-100 text-red-600',
  rose: 'bg-rose-100 text-rose-600',
  amber: 'bg-amber-100 text-amber-600',
  teal: 'bg-teal-100 text-teal-600',
  stone: 'bg-stone-100 text-stone-600',
};

export interface StatCardProps {
  title: string;
  value: string | number;
  /** Optional Lucide icon (or any node) rendered in a tinted box. */
  icon?: ReactNode;
  /** Tint for the icon box. Defaults to a neutral stone. */
  tone?: StatTone;
  /**
   * Optional trend delta as a percentage. Only rendered when a number is
   * passed — omit it entirely for metrics without a comparison (many of our
   * endpoints don't compute deltas yet, so a fake "+0.0%" would mislead).
   */
  change?: number;
  /** Label after the trend %, e.g. "vs last period". */
  changeLabel?: string;
  subtitle?: string;
  /** When set, the whole tile becomes a link to this in-app path. */
  to?: string;
  /** Standalone Card presentation (shadow + ring hover) for detail-page KPI rows. */
  elevated?: boolean;
  className?: string;
}

export default function StatCard({
  title,
  value,
  icon,
  tone = 'stone',
  change,
  changeLabel = 'vs last period',
  subtitle,
  to,
  elevated = false,
  className,
}: StatCardProps) {
  const hasTrend = typeof change === 'number' && Number.isFinite(change);
  const isPositive = hasTrend && (change as number) >= 0;

  const body = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-stone-500 truncate">{title}</p>
        <p className="mt-1 text-2xl font-bold text-ink">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-stone-500">{subtitle}</p>}
        {hasTrend && (
          <div className="mt-2 flex items-center gap-1">
            {isPositive ? (
              <TrendingUp className="h-4 w-4 text-green-600" aria-hidden="true" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" aria-hidden="true" />
            )}
            <span
              className={cn(
                'text-sm font-medium',
                isPositive ? 'text-green-600' : 'text-red-600'
              )}
            >
              {isPositive ? '+' : ''}
              {(change as number).toFixed(1)}%
            </span>
            {changeLabel && (
              <span className="ml-1 text-xs text-stone-400">{changeLabel}</span>
            )}
          </div>
        )}
      </div>
      {icon && (
        <div className={cn('shrink-0 rounded-lg', toneStyles[tone], elevated ? 'p-3' : 'p-2.5')}>
          {/* In tile mode force a consistent icon size; in elevated mode respect
              the size the caller passed on the icon. */}
          <span aria-hidden="true" className={cn('block', !elevated && '[&>svg]:h-5 [&>svg]:w-5')}>
            {icon}
          </span>
        </div>
      )}
    </div>
  );

  if (elevated) {
    const card = <Card padding="md">{body}</Card>;
    return to ? (
      <Link
        to={to}
        className={cn(
          'block rounded-xl transition-shadow hover:ring-2 hover:ring-green-200',
          className
        )}
      >
        {card}
      </Link>
    ) : (
      <div className={className}>{card}</div>
    );
  }

  const base = 'rounded-lg border border-line bg-surface p-4';
  if (to) {
    return (
      <Link
        to={to}
        className={cn(base, 'block transition-colors hover:bg-stone-50', className)}
      >
        {body}
      </Link>
    );
  }
  return <div className={cn(base, className)}>{body}</div>;
}
